import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Project, Lead } from '../../types';
import Input from '../ui/Input';
import { Search, X, User, Plus } from 'lucide-react';
import { useI18n } from '../../context/I18nContext';
import ProjectTypeIcon from '../ui/ProjectTypeIcon';
import ProjectStatusBadge from '../projects/ProjectStatusBadge';
import type { RequestCreateProjectArgs } from '../../hooks/useCreateProjectFromSearch';
import { useSearchCreateFocus } from '../../hooks/useSearchCreateFocus';
import { useFollowTriggerPosition } from '../../hooks/useFollowTriggerPosition';

interface ProjectSearchProps {
  projects: Project[];
  leads: Lead[];
  selectedProject: Project | null;
  onSelectProject: (project: Project | null) => void;
  searchFn?: (term: string) => Promise<Project[]>;
  placeholder?: string;
  error?: string;
  /** Sticky "Créer un projet" footer — typically useCreateProjectFromSearch().requestCreateProject */
  onCreateProject?: (args: RequestCreateProjectArgs) => void;
}

const ProjectSearch: React.FC<ProjectSearchProps> = ({
  projects,
  leads,
  selectedProject,
  onSelectProject,
  searchFn,
  placeholder,
  error,
  onCreateProject,
}) => {
  const { t } = useI18n();
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [remoteProjects, setRemoteProjects] = useState<Project[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const searchSeqRef = useRef(0);
  const selectedProjectRef = useRef(selectedProject);
  selectedProjectRef.current = selectedProject;
  const { inputRef, shouldOpenOnFocus, commitSelectionUi } = useSearchCreateFocus();
  const menuRef = useRef<HTMLDivElement>(null);

  const computeMenuPosition = useCallback((rect: DOMRect) => ({
    top: rect.bottom + 4,
    left: rect.left,
    width: rect.width,
  }), []);

  const menuPosition = useFollowTriggerPosition({
    open: isOpen,
    triggerRef: inputRef,
    menuRef,
    computePosition: computeMenuPosition,
  });

  useEffect(() => {
    setSearchTerm(selectedProject ? selectedProject.name : '');
  }, [selectedProject]);

  useEffect(() => {
    if (!searchFn || !isOpen) return;

    const seq = ++searchSeqRef.current;
    const term = searchTerm.trim();
    setIsSearching(true);

    const handle = setTimeout(() => {
      searchFn(term)
        .then((res) => {
          if (searchSeqRef.current !== seq) return;
          setRemoteProjects(res || []);
          setIsSearching(false);
        })
        .catch(() => {
          if (searchSeqRef.current !== seq) return;
          setRemoteProjects([]);
          setIsSearching(false);
        });
    }, term ? 250 : 0);

    return () => clearTimeout(handle);
  }, [searchFn, searchTerm, isOpen]);

  const filteredProjects = useMemo(() => {
    if (searchFn) return remoteProjects.slice(0, 10);
    if (!searchTerm.trim()) return projects.slice(0, 10);

    const search = searchTerm.toLowerCase();
    return projects
      .filter((project) => {
        const lead = leads.find((l) => l.id === project.leadId);
        return (
          project.name.toLowerCase().includes(search) ||
          (lead?.name.toLowerCase() || '').includes(search)
        );
      })
      .slice(0, 10);
  }, [projects, leads, searchTerm, searchFn, remoteProjects]);

  const handleSelect = (project: Project) => {
    setSearchTerm(project.name);
    setIsOpen(false);
    onSelectProject(project);
  };

  const handleClear = () => {
    setSearchTerm('');
    setIsOpen(false);
    onSelectProject(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setIsOpen(true);
  };

  const handleInputFocus = () => {
    const current = selectedProjectRef.current;
    const committed = !!(current && searchTerm === current.name);
    if (shouldOpenOnFocus(committed)) setIsOpen(true);
  };

  const handleInputBlur = () => {
    setTimeout(() => {
      setIsOpen(false);
      const current = selectedProjectRef.current;
      if (current) {
        setSearchTerm((term) => (term !== current.name ? current.name : term));
      }
    }, 200);
  };

  const handleCreateProject = () => {
    if (!onCreateProject) return;
    const selectedDisplay = selectedProject?.name ?? '';
    const prefillName =
      searchTerm.trim() && searchTerm.trim() !== selectedDisplay ? searchTerm.trim() : '';
    setIsOpen(false);
    onCreateProject({
      prefillName,
      onCreated: (project) => {
        setIsOpen(false);
        onSelectProject(project);
        commitSelectionUi();
      },
    });
  };

  const showCreate = typeof onCreateProject === 'function';
  const selectedDisplay = selectedProject?.name ?? '';
  const namedPrefill =
    searchTerm.trim() && searchTerm.trim() !== selectedDisplay ? searchTerm.trim() : '';

  return (
    <div className="relative">
      <div className="relative">
        <Input
          ref={inputRef}
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          placeholder={placeholder}
          icon={<Search className="w-4 h-4" />}
          error={error}
        />
        {selectedProject && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {isOpen &&
        menuPosition &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={menuRef}
            style={{
              position: 'fixed',
              top: menuPosition.top,
              left: menuPosition.left,
              width: menuPosition.width,
              zIndex: 9999,
            }}
            className="bg-white border border-gray-200 rounded-xl shadow-paper overflow-hidden flex flex-col max-h-72"
          >
            <div className="overflow-y-auto overflow-x-hidden min-h-0 flex-1">
              {searchFn && isSearching ? (
                <div className="px-4 py-3 text-sm text-gray-500 text-center flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-gray-300 border-t-primary-500 rounded-full animate-spin" />
                  {t('common.loading')}
                </div>
              ) : filteredProjects.length === 0 ? (
                <div className="px-4 py-3 text-sm text-gray-500 text-center">
                  {t('projects.noProjectsFound')}
                </div>
              ) : (
                filteredProjects.map((project) => {
                  const lead = leads.find((l) => l.id === project.leadId);
                  return (
                    <div
                      key={project.id}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handleSelect(project)}
                      className="group px-4 py-3 hover:bg-primary-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                    >
                      <div className="flex items-center space-x-3">
                        <ProjectTypeIcon type={project.type} showTooltip />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate group-hover:text-primary-700 transition-colors">{project.name}</p>
                            <ProjectStatusBadge
                              status={project.status}
                              className="shrink-0 max-w-[9rem] truncate"
                            />
                          </div>
                          <div className="flex items-center space-x-1 text-xs text-gray-500 mt-1">
                            <User className="w-3 h-3 shrink-0" />
                            <span className="truncate">{lead?.name || '—'}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {showCreate && (
              <div className="shrink-0 border-t border-gray-200 bg-white">
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={handleCreateProject}
                  className="w-full flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-primary-700 hover:bg-primary-50 transition-colors"
                >
                  <Plus className="w-4 h-4 shrink-0" strokeWidth={2.25} />
                  <span className="truncate">
                    {namedPrefill
                      ? t('projects.createNamed', { name: namedPrefill })
                      : t('projects.createNew')}
                  </span>
                </button>
              </div>
            )}
          </div>,
          document.body
        )}
    </div>
  );
};

export default ProjectSearch;
